import { useEffect, useRef } from 'react';

const DEFAULT_LANGUAGE = 'en';

interface ISpotLocalizationEntry {
  url?: string;
  location?: string;
  previewImage?: string;
  id?: string;
  md5?: string;
}

type SpotLocalizationsShape = Record<string, ISpotLocalizationEntry>;

interface ILocalizableSpot {
  type?: string;
  data?: {
    location?: string;
    url?: string;
    id?: string;
    [k: string]: unknown;
  };
  previewImage?: string;
  md5?: string;
  localizations?: SpotLocalizationsShape;
  brandingSpotId?: string;
  roomSpotId?: string;
  [k: string]: unknown;
}

const isVideoSpot = (spot: ILocalizableSpot): boolean => {
  const upper = (spot.type ?? '').toUpperCase();
  return upper === 'VIDEO' || upper === 'VIDEO_STATIC';
};

export const localizeSpotForLanguage = (
  spot: ILocalizableSpot,
  languageCode: string,
): ILocalizableSpot => {
  const overrides = spot.localizations;
  if (!overrides) {
    return spot;
  }

  const override = overrides[languageCode] ?? overrides[DEFAULT_LANGUAGE];
  if (!override || (!override.url && !override.location)) {
    return spot;
  }

  const localizedData = {
    ...(spot.data ?? {}),
    ...(override.url ? { url: override.url } : {}),
    ...(override.location ? { location: override.location } : {}),
    ...(override.id ? { id: override.id } : {}),
  };

  const next: ILocalizableSpot = {
    ...spot,
    data: localizedData,
    ...(override.md5 ? { md5: override.md5 } : {}),
  };

  if (isVideoSpot(spot)) {
    if (override.previewImage) {
      next.previewImage = override.previewImage;
    } else {
      const fallbackPreview =
        overrides[DEFAULT_LANGUAGE]?.previewImage ?? spot.previewImage;
      if (fallbackPreview) {
        next.previewImage = fallbackPreview;
      }
    }
  }

  return next;
};

const localizeSpotList = (
  spots: ILocalizableSpot[],
  languageCode: string,
): ILocalizableSpot[] =>
  spots.map((spot) => localizeSpotForLanguage(spot, languageCode));

interface UseSpotLanguageSyncArgs<TSpot extends ILocalizableSpot> {
  language: string | undefined;
  isEventActive: boolean;
  brandingSpots: TSpot[];
  roomSpots: TSpot[];
  setBrandingSpots: (spots: TSpot[]) => void;
  setRoomSpots: (spots: TSpot[]) => void;
  sendMessageToUnreal: (message: Record<string, unknown>) => void;
  currentBrandingId?: string | null;
  isBrandingModeEnabled?: boolean;
}

export const useSpotLanguageSync = <TSpot extends ILocalizableSpot>({
  language,
  isEventActive,
  brandingSpots,
  roomSpots,
  setBrandingSpots,
  setRoomSpots,
  sendMessageToUnreal,
  currentBrandingId,
  isBrandingModeEnabled,
}: UseSpotLanguageSyncArgs<TSpot>): void => {
  const previousLanguageRef = useRef<string | null>(null);

  useEffect(() => {
    if (!language) {
      return;
    }

    if (previousLanguageRef.current === null) {
      previousLanguageRef.current = language;
      return;
    }

    if (previousLanguageRef.current === language) {
      return;
    }

    previousLanguageRef.current = language;

    if (!isEventActive) {
      return;
    }

    const nextBrandingSpots = localizeSpotList(
      brandingSpots,
      language,
    ) as TSpot[];
    const nextRoomSpots = localizeSpotList(roomSpots, language) as TSpot[];

    setBrandingSpots(nextBrandingSpots);
    setRoomSpots(nextRoomSpots);

    const activeSpots =
      currentBrandingId && isBrandingModeEnabled
        ? nextBrandingSpots
        : nextRoomSpots;

    if (activeSpots.length > 0) {
      sendMessageToUnreal({
        action: 'SPOTS_UPDATED',
        spots: [],
      });
    }
  }, [
    language,
    isEventActive,
    brandingSpots,
    roomSpots,
    setBrandingSpots,
    setRoomSpots,
    sendMessageToUnreal,
    currentBrandingId,
    isBrandingModeEnabled,
  ]);
};
